import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TranslationKey } from '@core/enums/translation-key.enum';
import { TableColumn } from '@smartera/core';

@Injectable({
	providedIn: 'root',
})
export class ColumnsTranslationService {
	constructor(public translationService: TranslateService) {}

	public translateColumnValue<T>(value: unknown, col?: TableColumn<T>, rowData?: T): string {
		if (!col) {
			return String(value ?? '');
		}

		if (col.renderMethod && rowData) {
			return String(col.renderMethod(rowData));
		}

		if (!col.translate) {
			return String(value ?? '');
		}

		const key = this.buildTranslationKey(value, col);
		return key ? this.t(key) : String(value ?? '');
	}

	// get translated value
	public getValue(value: any, key: TranslationKey, col?: TableColumn<any>, rowData?: any) {
		if (!key) {
			return value;
		}

		switch (key) {
			case TranslationKey.valid:
				if (value === true || value === 'VALID') {
					return this.t('form.radio.active');
				} else {
					return this.t('form.radio.disable');
				}

			case TranslationKey.shipmentStatus:
				switch (value) {
					case 'COMPLETED':
						return this.t('complete');
					case 'EMPTY_WEIGHED':
						return this.t('empty-weighed');
					case 'LOADED_WEIGHED':
						return this.t('loaded-weighed');

					default:
						return this.t('ready-shipment');
				}

			case TranslationKey.purchaseBill: {
				if (['ONWAY', 'ARRIVED'].includes(value)) {
					if (rowData.transfer) {
						// transfer
						return this.translateByKey('bill-transfer-' + String(value).toLowerCase(), col);
					} else {
						// system
						return this.translateByKey('bill-system-' + String(value).toLowerCase(), col);
					}
				} else {
					if (value) {
						return this.translateByKey(value, col);
					} else {
						return '';
					}
				}
			}
			//
			case TranslationKey.pricingType:
				if (value === 'NEW') {
					return this.t('pricing-new');
				} else {
					return this.t('pricing-approve');
				}
			case TranslationKey.hq:
				if (value === true) {
					return this.t('form.radio.yes');
				} else {
					return this.t('form.radio.no');
				}
			case TranslationKey.approved:
				if (value === 'APPROVE') {
					return this.t('pages.invoice.master.approve');
				} else {
					// new
					return this.t('pages.invoice.master.new');
				}
			case TranslationKey.accountSource:
				switch (value) {
					case 'INV_PAYMENT':
						return this.t('inv-payment');
						break;
					case 'INV_PAYMENT_CANCEL':
						return this.t('inv-payment-cancel');
						break;
					case 'INV_COLLECTION':
						return this.t('inv-collection');
						break;
					case 'INV_COLLECTION_CANCEL':
						return this.t('inv-collection-cancel');
						break;
					case 'PURCHASE_INV':
						return this.t('purchase-inv');
						break;
					case 'PURCHASE_RETURN':
						return this.t('purchase-return');
						break;

					case 'SALE_INV_CANCEL':
						return this.t('sale-inv-cancel');
						break;
					case 'SALE_INV':
						return this.t('sale-inv');
						break;
					case 'SALE_RETURN':
						return this.t('sale-return');
						break;
					case 'VENDOR_SHIPPING_PRICING':
						return this.t('vendor-shipping-pricing');
					default:
						break;
				}

			case TranslationKey.draweeType:
				switch (value) {
					case 'CUSTOMER':
						return this.t('customer');
						break;
					case 'SUPPLIER':
						return this.t('supplier');
						break;
					case 'DEALER':
						return this.t('dealer');
						break;
					default:
						break;
				}
			case TranslationKey.validExist:
				if (value === true) {
					return this.t('form.radio.yes');
				} else {
					return this.t('form.radio.no');
				}
			case TranslationKey.purchaseType: {
				if (value === 'EXPENSE') {
					return this.t('purchase.type.expense');
				} else {
					return this.t('purchase.type.purchase');
				}
			}
			case TranslationKey.payType:
				return this.getPayType(value);
			case TranslationKey.collectionSource: {
				return this.t(value === 'SALE' ? 'collection.type.sale' : 'purchase.type.expense');
			}
			case TranslationKey.state:
				return this.t(this.getStateKey(value));
			case TranslationKey.scope:
				return this.t((value === 'Sku' ? 'sku-name' : value).toLowerCase());
			case 'reports-type':
				return this.t(value);
			case TranslationKey.key:
				return this.translateByKey(value, col);
			case TranslationKey.umId:
				switch (value) {
					case 'um.weight.kilogram':
						return this.t('um.weight.kilogram');
					case 'um.weight.quantity':
						return this.t('um.weight.quantity');
					case 'um.weight.gram':
						return this.t('um.weight.gram');
					case 'um.weight.ton':
						return this.t('um.weight.ton');
					default:
						return '';
				}
			case TranslationKey.tracingStatus:
				switch (value) {
					case 'ERROR':
						return this.t('tracing-status.error');
					case 'COMPLETED':
						return this.t('tracing-status.completed');
					case 'PROCESSING':
						return this.t('tracing-status.processing');
				}
			case TranslationKey.einvoiceDocumentType:
				switch (value) {
					case 'INVOICE':
						return this.t('eInvoice');
					case 'ARCHIVE':
						return this.t('eArchive');
					default:
						return '';
				}
			case TranslationKey.einvoiceSendStatus:
				switch (value) {
					case 'SENT':
						return this.t('sent');
					case 'DO_EDIT':
						return this.t('do-edit');
					case 'NOT_SEND':
						return this.t('not-send');
					case 'SERIAL_ASSIGNED':
						return this.t('serial-assigned');
					case 'DRAFT':
						return this.t('draft');
					default:
						return '';
				}
			case TranslationKey.einvoiceDeliveryStatus:
				switch (value) {
					case 'IN_PROGRESS':
						return this.t('in-progress');
					case 'COMPLETED':
						return this.t('completed-einvoice');
					case 'WAITING_RESPONSE':
						return this.t('waiting-response');
					default:
						return '';
				}
			case TranslationKey.einvoiceMainType:
				switch (value) {
					case 'TEMELFATURA':
						return this.t('temel-fatura');
					case 'TICARIFATURA':
						return this.t('ticari-fatura');
					default:
						return '';
				}
			case TranslationKey.saleOrderMaster:
				switch (value) {
					case 'NEW':
						return this.t('pages.invoice.master.new');
					case 'APP':
						return this.t('pages.invoice.master.approve');
					case 'CANCEL':
						return this.t('pages.invoice.master.cancel');
					case 'DENY':
						return this.t('deny');
					default:
						return '';
				}
			case TranslationKey.shippingStatus:
				switch (value) {
					case 'NEW':
						return this.t('shipping-status.new');
					case 'SENT_TO_FARM':
						return this.t('shipping-status.sent_to_farm');
					case 'RETURNED_FROM_FARM':
						return this.t('shipping-status.returned_from_farm');
					case 'SENT_TO_UNLOAD':
						return this.t('shipping-status.sent_to_unload');
					case 'UNLOAD_COMPLETED':
						return this.t('shipping-status.unload_completed');
					case 'WEIGHED':
						return this.t('shipping-status.weighed');
					case 'PRICED':
						return this.t('shipping-status.priced');
					case 'COMPLETED':
						return this.t('shipping-status.completed');
					case 'CANCELED':
						return this.t('shipping-status.canceled');
					default:
						return '';
				}
			case TranslationKey.shippingDetailType:
				switch (value) {
					case 'FIRST_SEPARATED_QTY':
						return this.t('firstQuality');
					case 'SECOND_SEPARATED_QTY':
						return this.t('secondQuality');
					case 'THIRD_SEPARATED_QTY':
						return this.t('thirdQuality');
					case 'FOURTH_SEPARATED_QTY':
						return this.t('fourthQuality');
					case 'STALK_QTY':
						return this.t('stalk');
					default:
						return '';
				}
			case TranslationKey.accountTransactionType:
				switch (value) {
					case 'ALIS_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'ALIS_BORC':
						return this.t('account-transaction-type.' + value);
					case 'ALIS_IADE_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'ALIS_IADE_BORC':
						return this.t('account-transaction-type.' + value);
					case 'ALIS_IPT_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'ALIS_IPT_BORC':
						return this.t('account-transaction-type.' + value);
					case 'ODEME_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'ODEME_BORC':
						return this.t('account-transaction-type.' + value);
					case 'ODEME_IPT_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'ODEME_IPT_BORC':
						return this.t('account-transaction-type.' + value);
					case 'SATIS_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'SATIS_BORC':
						return this.t('account-transaction-type.' + value);
					case 'SATIS_IADE_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'SATIS_IADE_BORC':
						return this.t('account-transaction-type.' + value);
					case 'SATIS_IPT_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'SATIS_IPT_BORC':
						return this.t('account-transaction-type.' + value);
					case 'STOPAJ_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'STOPAJ_BORC':
						return this.t('account-transaction-type.' + value);
					case 'TAHSILAT_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'TAHSILAT_BORC':
						return this.t('account-transaction-type.' + value);
					case 'TAHSILAT_IPT_ALACAK':
						return this.t('account-transaction-type.' + value);
					case 'TAHSILAT_IPT_BORC':
						return this.t('account-transaction-type.' + value);
					default:
						return '';
				}
			case TranslationKey.deliveryDateBase:
				if (value === 'MASTER') {
					return this.t('deliveryBaseMaster');
				}
				return this.t('deliveryBaseDetail');
			case TranslationKey.allowDetailType:
				switch (value) {
					case 'SKU':
						return this.t('allowDetailSku');
					case 'SERVICE':
						return this.t('allowDetailService');
					case 'MIX':
						return this.t('allowDetailMix');
					default:
						return '';
				}
			case TranslationKey.deliveryDateAssign:
				return value === 'AUTO' ? this.t('deliveryDateAutomatic') : this.t('priceBaseOnManuel');
			default:
				break;
		}
	}

	private buildTranslationKey<T>(value: unknown, col: TableColumn<T>): string {
		if (col.type === 'valid') {
			return (col.prefix || 'form.radio.') + this.getValidSuffix(value);
		}

		if (col.prefix === 'state.') {
			const suffix = this.getStateSuffix(value);
			return suffix ? `${col.prefix}${suffix}` : '';
		}

		const normalizedValue = String(value ?? '').toLowerCase();
		return `${col.prefix || ''}${normalizedValue}`;
	}

	private getValidSuffix(value: unknown): 'active' | 'disable' {
		if (value === true || value === 'VALID') {
			return 'active';
		}

		return 'disable';
	}

	private getStateSuffix(value: unknown): string {
		switch (value) {
			case 'VALID':
			case 'ACTIVE':
				return 'active';
			case 'NEW':
				return 'new';
			case 'NOT_ACTIVATE':
				return 'not_activate';
			case 'LOCKED':
				return 'locked';
			case 'CLOSED':
				return 'closed';
			case 'DISABLED':
				return 'disabled';
			case 'CANCELLED':
			case 'CANCELED':
				return 'cancel';
			default:
				return '';
		}
	}

	public translateByKey(value, col) {
		let prefix = '';
		if (col && col.prefix) {
			prefix = col.prefix;
		}
		return this.t(prefix + String(value).toLowerCase());
	}
	public t(key: string): string {
		return this.translationService.instant(key);
	}

	public getPayType(type: string): string {
		switch (type) {
			case 'CASH':
				return this.t('bank-type.cash');
			case 'CREDIT CARD':
			case 'CREDIT_CARD':
				return this.t('bank-type.credit-card');
			case 'BANK':
				return this.t('bank-type.bank');
		}
	}

	// public getWorkflowStatusType(type: string): string {

	public getStateKey(state): string {
		switch (state) {
			case 'VALID':
			case 'ACTIVE':
				return 'state.active';
			case 'NEW':
				return 'state.new';
			case 'NOT_ACTIVATE':
				return 'state.not_activate';
			case 'LOCKED':
				return 'state.locked';
			case 'CLOSED':
				return 'state.closed';
			case 'DISABLED':
				return 'state.disabled';
			case 'CANCELLED':
				return 'state.cancel';
			case 'CANCELED':
				return 'state.cancel';
			default:
				return '';
		}
	}
}
